import type { RecoveryClassSlug, RecoveryDefinition, RecoveryMsId } from './types.js';

function def(
  entry: RecoveryDefinition,
): RecoveryDefinition {
  return entry;
}

/** P1 MS-REC definitions — 085/086 are P2 stubs per Phase 3 scope. */
export const RECOVERY_DEFINITIONS: readonly RecoveryDefinition[] = [
  def({
    msId: 'MS-REC-070',
    classSlug: 'session-expired',
    componentName: 'SessionExpiredRecovery',
    trigger: 'HTTP 401 / session refresh failure',
    primaryCta: 'sign-in',
    secondaryCta: 'shop',
    contextPreserved: ['returnTarget', 'tenant', 'journey'],
    analyticsEvents: ['error_shown', 'recovery_session_expired_shown', 'recovery_primary_cta'],
    i18nKeyPrefix: 'retail.rec.sessionExpired',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-071',
    classSlug: 'offline',
    componentName: 'OfflineRecovery',
    trigger: 'navigator.onLine === false',
    primaryCta: 'retry',
    secondaryCta: 'shop',
    contextPreserved: ['returnTarget', 'tenant', 'journey'],
    analyticsEvents: ['error_shown', 'recovery_offline_shown'],
    i18nKeyPrefix: 'retail.rec.offline',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-072',
    classSlug: 'no-network',
    componentName: 'NoNetworkRecovery',
    trigger: 'slow / unreachable network (distinct from offline)',
    primaryCta: 'retry',
    contextPreserved: ['returnTarget', 'tenant'],
    analyticsEvents: ['error_shown', 'recovery_no_network_shown'],
    i18nKeyPrefix: 'retail.rec.noNetwork',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-073',
    classSlug: 'maintenance',
    componentName: 'MaintenanceRecovery',
    trigger: 'HTTP 503 / maintenance flag',
    primaryCta: 'retry',
    contextPreserved: ['tenant'],
    analyticsEvents: ['error_shown', 'recovery_maintenance_shown'],
    i18nKeyPrefix: 'retail.rec.maintenance',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-074',
    classSlug: 'access-denied',
    componentName: 'AccessDeniedRecovery',
    trigger: 'HTTP 403 / entitlement block',
    primaryCta: 'sign-in',
    secondaryCta: 'shop',
    contextPreserved: ['returnTarget', 'tenant'],
    analyticsEvents: ['error_shown', 'recovery_access_denied_shown'],
    i18nKeyPrefix: 'retail.rec.accessDenied',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-075',
    classSlug: 'invalid-qr',
    componentName: 'InvalidQrRecovery',
    trigger: 'invalid / expired QR payment payload',
    primaryCta: 'retry',
    secondaryCta: 'cancel',
    contextPreserved: ['tenant', 'paymentId', 'journey'],
    analyticsEvents: ['error_shown', 'recovery_invalid_qr_shown'],
    i18nKeyPrefix: 'retail.rec.qrInvalid',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-076',
    classSlug: 'unknown-error',
    componentName: 'UnknownErrorRecovery',
    trigger: 'unhandled exception / ErrorBoundary',
    // Safe shop escape — never reload as primary (reload re-enters the same failure).
    primaryCta: 'shop',
    secondaryCta: 'support',
    contextPreserved: ['returnTarget', 'tenant', 'journey'],
    analyticsEvents: ['error_shown', 'recovery_unknown_shown'],
    i18nKeyPrefix: 'retail.rec.unknown',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-077',
    classSlug: 'update-required',
    componentName: 'UpdateRequiredRecovery',
    trigger: 'service worker update ready while app stale',
    primaryCta: 'reload',
    contextPreserved: ['tenant', 'paymentId', 'journey'],
    analyticsEvents: ['error_shown', 'recovery_update_required_shown', 'pwa_update_deferred'],
    i18nKeyPrefix: 'retail.rec.updateRequired',
    presentation: 'banner',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-078',
    classSlug: 'post-kiosk-invalid',
    componentName: 'PostKioskInvalidRecovery',
    trigger: 'invalid / expired post-kiosk token',
    primaryCta: 'shop',
    secondaryCta: 'support',
    contextPreserved: ['tenant'],
    analyticsEvents: ['error_shown', 'recovery_post_kiosk_invalid_shown'],
    i18nKeyPrefix: 'retail.rec.postKioskInvalid',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-079',
    classSlug: 'handoff-expired',
    componentName: 'HandoffExpiredRecovery',
    trigger: 'checkout handoff session TTL exceeded',
    primaryCta: 'shop',
    secondaryCta: 'sign-in',
    contextPreserved: ['returnTarget', 'tenant'],
    analyticsEvents: ['error_shown', 'recovery_handoff_expired_shown'],
    i18nKeyPrefix: 'retail.rec.handoffExpired',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-080',
    classSlug: 'payment',
    componentName: 'PaymentRecovery',
    trigger: 'payment terminal ambiguous / timeout',
    primaryCta: 'resume',
    secondaryCta: 'cancel',
    contextPreserved: ['tenant', 'paymentId', 'journey', 'returnTarget'],
    analyticsEvents: ['error_shown', 'recovery_payment_shown'],
    i18nKeyPrefix: 'retail.rec.payment',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-081',
    classSlug: 'tenant',
    componentName: 'TenantRecovery',
    trigger: 'tenant unavailable / context loss',
    primaryCta: 'tenant-select',
    contextPreserved: ['returnTarget'],
    analyticsEvents: ['error_shown', 'recovery_tenant_shown'],
    i18nKeyPrefix: 'retail.rec.tenant',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-082',
    classSlug: 'interrupted',
    componentName: 'InterruptedJourneyRecovery',
    trigger: 'journey checkpoint restore / refresh mid-flow',
    primaryCta: 'resume',
    secondaryCta: 'shop',
    contextPreserved: ['returnTarget', 'tenant', 'journey', 'paymentId'],
    analyticsEvents: ['error_shown', 'recovery_interrupted_shown'],
    i18nKeyPrefix: 'retail.rec.interrupted',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-083',
    classSlug: 'login-identity',
    componentName: 'LoginIdentityRecovery',
    trigger: 'HTTP 429 auth rate-limit / identity recovery',
    primaryCta: 'sign-in',
    secondaryCta: 'register',
    contextPreserved: ['returnTarget', 'tenant', 'journey'],
    analyticsEvents: ['error_shown', 'recovery_login_identity_shown'],
    i18nKeyPrefix: 'retail.rec.identity',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-084',
    classSlug: 'permission-required',
    componentName: 'PermissionRequiredRecovery',
    trigger: 'browser permission denied (camera, notifications)',
    primaryCta: 'retry',
    secondaryCta: 'cancel',
    contextPreserved: ['tenant', 'journey'],
    analyticsEvents: ['error_shown', 'recovery_permission_required_shown'],
    i18nKeyPrefix: 'retail.rec.permission',
    presentation: 'route',
    priority: 'P1',
  }),
  def({
    msId: 'MS-REC-085',
    classSlug: 'expired-permission',
    componentName: 'ExpiredPermissionRecovery',
    trigger: 'expired OAuth / step-up permission (P2)',
    primaryCta: 'sign-in',
    contextPreserved: ['tenant'],
    analyticsEvents: ['recovery_expired_permission_stub'],
    i18nKeyPrefix: 'retail.rec.expiredPermission',
    presentation: 'stub',
    priority: 'P2',
  }),
  def({
    msId: 'MS-REC-086',
    classSlug: 'generic-retry',
    componentName: 'GenericRetryRecovery',
    trigger: 'generic retry without offline classification (P2)',
    primaryCta: 'retry',
    secondaryCta: 'cancel',
    contextPreserved: ['tenant'],
    analyticsEvents: ['recovery_generic_retry_stub'],
    i18nKeyPrefix: 'retail.rec.retry',
    presentation: 'stub',
    priority: 'P2',
  }),
  def({
    msId: 'MS-REC-087',
    classSlug: 'general-error',
    componentName: 'GeneralBusinessErrorRecovery',
    trigger: 'classified API/business failure (ADR-013)',
    primaryCta: 'retry',
    secondaryCta: 'support',
    contextPreserved: ['returnTarget', 'tenant', 'paymentId', 'journey'],
    analyticsEvents: ['error_shown', 'recovery_general_error_shown'],
    i18nKeyPrefix: 'retail.rec.generalError',
    presentation: 'route',
    priority: 'P1',
  }),
] as const;

const bySlug = new Map<RecoveryClassSlug, RecoveryDefinition>(
  RECOVERY_DEFINITIONS.map((row) => [row.classSlug, row]),
);

const byMsId = new Map<RecoveryMsId, RecoveryDefinition>(
  RECOVERY_DEFINITIONS.map((row) => [row.msId, row]),
);

export function getRecoveryByClassSlug(
  classSlug: string | undefined,
): RecoveryDefinition | undefined {
  if (classSlug === undefined || classSlug.length === 0) {
    return undefined;
  }
  return bySlug.get(classSlug as RecoveryClassSlug);
}

export function getRecoveryByMsId(msId: RecoveryMsId): RecoveryDefinition | undefined {
  return byMsId.get(msId);
}

export const P1_RECOVERY_MS_IDS = RECOVERY_DEFINITIONS.filter((row) => row.priority === 'P1').map(
  (row) => row.msId,
);
